var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var ToolValidationResult_1;
import { JSON, JsonSchemaV2 } from '@symon-ai/wisepipe-serialize';
import { toolValidationResultSchemaValidation } from '../../validations.generated';
// the order of this enum matters. higher values are more severe and are shown with priority on the UI
export var ToolValidationState;
(function (ToolValidationState) {
    ToolValidationState[ToolValidationState["VALID"] = 0] = "VALID";
    ToolValidationState[ToolValidationState["INVALID"] = 1] = "INVALID";
    ToolValidationState[ToolValidationState["BAD_METADATA"] = 2] = "BAD_METADATA";
    ToolValidationState[ToolValidationState["EMPTY"] = 3] = "EMPTY";
    // These three will never be assigned by the tool itself
    ToolValidationState[ToolValidationState["PORT_MISMATCH"] = 4] = "PORT_MISMATCH";
    ToolValidationState[ToolValidationState["PORT_MISSING_EDGES"] = 5] = "PORT_MISSING_EDGES";
    ToolValidationState[ToolValidationState["TWO_INCOMING_EDGES"] = 6] = "TWO_INCOMING_EDGES";
    ToolValidationState[ToolValidationState["CYCLE"] = 7] = "CYCLE";
    ToolValidationState[ToolValidationState["SPARK_UNAVAILABLE"] = 8] = "SPARK_UNAVAILABLE";
    ToolValidationState[ToolValidationState["DEPRECATED"] = 9] = "DEPRECATED";
    ToolValidationState[ToolValidationState["MISSING_INCOMING_EDGE"] = 10] = "MISSING_INCOMING_EDGE";
    ToolValidationState[ToolValidationState["NO_COLUMN_TYPE_FOUND"] = 11] = "NO_COLUMN_TYPE_FOUND";
    ToolValidationState[ToolValidationState["MULTI_PORT_NOT_USED_INCOMING_EDGE"] = 12] = "MULTI_PORT_NOT_USED_INCOMING_EDGE";
    ToolValidationState[ToolValidationState["MULTI_PORT_UNMAPPED_INPUT"] = 13] = "MULTI_PORT_UNMAPPED_INPUT";
    ToolValidationState[ToolValidationState["MULTI_PORT_UNMAPPED_OUTPUT"] = 14] = "MULTI_PORT_UNMAPPED_OUTPUT";
    ToolValidationState[ToolValidationState["MULTI_PORT_MAPPED_INPUT_MISSING"] = 15] = "MULTI_PORT_MAPPED_INPUT_MISSING";
    ToolValidationState[ToolValidationState["MULTI_PORT_MAPPED_COLUMN_INVALID"] = 16] = "MULTI_PORT_MAPPED_COLUMN_INVALID";
    ToolValidationState[ToolValidationState["REUSABLE_LOGIC_EXTRA_PARAMETER_VALUE"] = 17] = "REUSABLE_LOGIC_EXTRA_PARAMETER_VALUE";
    ToolValidationState[ToolValidationState["REUSABLE_LOGIC_PARAMETER_VALUE_TYPE_MISMATCH"] = 18] = "REUSABLE_LOGIC_PARAMETER_VALUE_TYPE_MISMATCH";
    ToolValidationState[ToolValidationState["REUSABLE_LOGIC_UNMAPPED_INPUT"] = 19] = "REUSABLE_LOGIC_UNMAPPED_INPUT";
    ToolValidationState[ToolValidationState["REUSABLE_LOGIC_UNMAPPED_OUTPUT"] = 20] = "REUSABLE_LOGIC_UNMAPPED_OUTPUT";
    ToolValidationState[ToolValidationState["REUSABLE_LOGIC_MAPPED_INPUT_MISSING"] = 21] = "REUSABLE_LOGIC_MAPPED_INPUT_MISSING";
    ToolValidationState[ToolValidationState["REUSABLE_LOGIC_MAPPED_COLUMN_INVALID"] = 22] = "REUSABLE_LOGIC_MAPPED_COLUMN_INVALID";
    ToolValidationState[ToolValidationState["REUSABLE_LOGIC_FORBIDDEN_TOOL"] = 23] = "REUSABLE_LOGIC_FORBIDDEN_TOOL";
    ToolValidationState[ToolValidationState["REUSABLE_LOGIC_CYCLE"] = 24] = "REUSABLE_LOGIC_CYCLE";
    ToolValidationState[ToolValidationState["REUSABLE_LOGIC_INNER_INVALID"] = 25] = "REUSABLE_LOGIC_INNER_INVALID"; // There is an invalid tool in the inner pipe of an RL
})(ToolValidationState || (ToolValidationState = {}));
export const toolValidationResultSchema = {
    $id: 'urn:wisepipe-common-pipe:ToolValidationResult',
    type: 'object',
    properties: {
        validationState: { type: 'number', enum: Object.values(ToolValidationState) },
        comment: { type: 'string' },
        suggestedFix: { type: 'string' }
    },
    required: ['validationState']
};
/**
 * Represents the result of validation for calc.
 * @param {ToolValidationState} validationState - Specifies the tool validation state.
 * @param {string} [comment] - An optional comment describing the validation result. This is used to provide details about the validation issues to the AI.
 * @param {string} [suggestedFix] - An optional suggested fix for the validation issues. This is used to provide a fix for the AI to apply to the tool.
 */
let ToolValidationResult = ToolValidationResult_1 = class ToolValidationResult {
    constructor(validationState, comment, suggestedFix) {
        this.validationState = validationState;
        if (comment !== undefined)
            this.comment = comment;
        if (suggestedFix !== undefined)
            this.suggestedFix = suggestedFix;
        JSON.validate(this, ToolValidationResult_1);
    }
    static getMissingSourceColumnsResult() {
        return new ToolValidationResult_1(ToolValidationState.BAD_METADATA, 'There are no source columns.');
    }
    static getValidResult(comment) {
        return new ToolValidationResult_1(ToolValidationState.VALID, comment);
    }
    static getBadMetadataResult(comment, suggestedFix) {
        return new ToolValidationResult_1(ToolValidationState.BAD_METADATA, comment, suggestedFix);
    }
    static getResultFromSourceValidation(sourceValidationResult) {
        return sourceValidationResult.isValid
            ? ToolValidationResult_1.getValidResult(sourceValidationResult.comment)
            : ToolValidationResult_1.getBadMetadataResult(sourceValidationResult.comment, sourceValidationResult.suggestedFix);
    }
    static getInvalidResult(comment, suggestedFix) {
        return new ToolValidationResult_1(ToolValidationState.INVALID, comment, suggestedFix);
    }
    static getPortMissingEdgesResult(comment, suggestedFix) {
        return new ToolValidationResult_1(ToolValidationState.PORT_MISSING_EDGES, comment, suggestedFix);
    }
};
ToolValidationResult = ToolValidationResult_1 = __decorate([
    JsonSchemaV2(toolValidationResultSchema, toolValidationResultSchemaValidation)
], ToolValidationResult);
export { ToolValidationResult };
//# sourceMappingURL=toolValidationResult.js.map